import React, { useMemo } from 'react';
import { LoadingIndicator } from '../../../LoadingIndicator';
import ConversationMessageComponent from '../Message';
import {
    MessagesContainer,
    UnreadDivider,
    UnreadDividerLine,
    UnreadDividerText
} from './styled';
import {
    ConversationMessage,
    MessageReaction
} from '../../../../../../types/chat';
import { useChatScroll } from '../../../../../../pages/Chat/hooks/useChatScroll';

type Props = {
    messages: ConversationMessage[];
    conversationId: number;
    isLoading: boolean;
    conversationError?: string;
    hoveredMessageId: number | null;
    onHover: (messageId: number | null) => void;
    onReply: (message: ConversationMessage) => void;
    onDelete: (message: ConversationMessage) => void;
    onReact: (messageId: number, emoji: string) => void;
    onMarkUnread: (message: ConversationMessage) => void;
    reactionsMap: Record<number, MessageReaction[]>;
    allowReactions: boolean;
    lastReadMessageId: number;
    unreadMessageCount: number;
    targetMessageId?: number | null;
    currentUserAlias?: string;
};

const ConversationMessageList: React.FC<Props> = ({
    messages,
    conversationId,
    isLoading,
    conversationError,
    hoveredMessageId,
    onHover,
    onReply,
    onDelete,
    onReact,
    onMarkUnread,
    reactionsMap,
    allowReactions,
    lastReadMessageId,
    unreadMessageCount,
    targetMessageId,
    currentUserAlias
}) => {
    const { scrollRef } = useChatScroll({
        messages: messages ?? [],
        conversationId,
        lastReadMessageId,
        targetMessageId
    });

    const firstUnreadIndex = useMemo(() => {
        if (unreadMessageCount === 0 || messages.length === 0) {
            return -1;
        }
        return messages.findIndex(
            (msg) => msg.id > lastReadMessageId && !msg.isOwn
        );
    }, [messages, lastReadMessageId, unreadMessageCount]);

    // Show loading only when there's no conversationId or no messages yet
    if (!conversationId) {
        return (
            <MessagesContainer ref={scrollRef}>
                <LoadingIndicator
                    isLoading
                    position="top"
                    message="Loading conversation..."
                />
            </MessagesContainer>
        );
    }

    if (conversationError) {
        return (
            <MessagesContainer ref={scrollRef}>
                <div
                    style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: 'red'
                    }}
                >
                    Error loading messages: {conversationError}
                </div>
            </MessagesContainer>
        );
    }

    if (messages.length === 0 && !isLoading) {
        return (
            <MessagesContainer ref={scrollRef}>
                <div
                    style={{
                        padding: '20px',
                        textAlign: 'center',
                        color: '#666'
                    }}
                >
                    No messages yet. Start the conversation!
                </div>
            </MessagesContainer>
        );
    }

    // Show loading indicator at top if loading and no messages yet
    const showTopLoading = isLoading && messages.length === 0;

    return (
        <MessagesContainer ref={scrollRef}>
            {showTopLoading && (
                <LoadingIndicator
                    isLoading
                    position="top"
                    message="Loading messages..."
                />
            )}
            {messages.map((message, index) => (
                <React.Fragment key={message.id}>
                    {index === firstUnreadIndex && (
                        <UnreadDivider>
                            <UnreadDividerLine />
                            <UnreadDividerText>
                                {unreadMessageCount} unread{' '}
                                {unreadMessageCount === 1
                                    ? 'message'
                                    : 'messages'}
                            </UnreadDividerText>
                            <UnreadDividerLine />
                        </UnreadDivider>
                    )}
                    <div data-message-id={message.id}>
                        <ConversationMessageComponent
                            message={message}
                            showSender={true}
                            isHovered={hoveredMessageId === message.id}
                            onHover={onHover}
                            onReply={onReply}
                            onDelete={onDelete}
                            onReact={onReact}
                            onMarkUnread={onMarkUnread}
                            allowReactions={allowReactions}
                            reactions={reactionsMap[message.id] ?? []}
                            currentUserAlias={currentUserAlias}
                        />
                    </div>
                </React.Fragment>
            ))}
        </MessagesContainer>
    );
};

export default ConversationMessageList;
